import { useRef, useState } from "react";
import { toast } from "sonner";
import { ScanLine, Loader2, RotateCcw, Check, Image as ImageIcon, Crop, Plus, X, CheckCheck, FileType2 } from "lucide-react";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { aiApi } from "./aiApi";
import AICornerEditor from "./AICornerEditor";

const dataUrlToFile = async (url, name) => new File([await (await fetch(url)).blob()], name, { type: "image/jpeg" });

function PageStrip({ pages, onRemove }) {
  if (!pages.length) return null;
  return (
    <div className="flex gap-2 overflow-x-auto pb-1" data-testid="ai-scan-pages">
      {pages.map((p, i) => (
        <div key={p.key} className="relative shrink-0" data-testid={`ai-scan-page-${i}`}>
          <img src={p.image} alt={`Halaman ${i + 1}`} className="h-16 w-12 object-cover rounded-lg border border-slate-200" />
          <span className="absolute bottom-0.5 left-0.5 rounded bg-fuchsia-600 text-white text-[9px] font-bold px-1">{i + 1}</span>
          <button onClick={() => onRemove(i)} className="absolute -top-1.5 -right-1.5 h-5 w-5 grid place-items-center rounded-full bg-white border border-slate-200 shadow" data-testid={`ai-scan-page-remove-${i}`}><X className="h-3 w-3" /></button>
        </div>
      ))}
    </div>
  );
}

export default function AIScanButton({ disabled, remaining = 10, onAdd, onToWord }) {
  const ref = useRef(null);
  const [open, setOpen] = useState(false);
  const [pages, setPages] = useState([]);
  const [cur, setCur] = useState(null);
  const [busy, setBusy] = useState("");
  const [adjust, setAdjust] = useState(false);
  const full = pages.length >= remaining;

  const pick = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setOpen(true); setAdjust(false); setCur({ raw: f }); setBusy("scan");
    try { const res = await aiApi.scan(f); setCur({ raw: f, res, auto: res.corners, original: res.original }); }
    catch (err) { toast.error(err.response?.data?.detail || "Gagal imbas gambar"); setCur(null); }
    finally { setBusy(""); }
  };
  const warp = async (pts) => {
    setBusy("warp");
    try { const res = await aiApi.scan(cur.raw, pts); setCur((c) => ({ ...c, res })); setAdjust(false); }
    catch (err) { toast.error(err.response?.data?.detail || "Gagal potong"); }
    finally { setBusy(""); }
  };
  const keep = async (scanned) => {
    const image = scanned ? cur.res.image : cur.original;
    setPages((p) => [...p, { key: Date.now(), image, file: scanned ? null : cur.raw }]);
    setCur(null);
  };
  const uploadAll = async () => {
    const out = [];
    for (const [i, p] of pages.entries()) out.push(await aiApi.upload(p.file || await dataUrlToFile(p.image, `imbasan_${Date.now()}_${i + 1}.jpg`)));
    return out;
  };
  const finish = async (toWord) => {
    setBusy(toWord ? "word" : "done");
    try {
      const up = await uploadAll();
      if (toWord) { onToWord(up); toast.success(`${up.length} halaman dihantar kepada Mimi untuk ditukar ke Word`); }
      else { up.forEach(onAdd); toast.success(`${up.length} halaman imbasan dilampirkan`); }
      setPages([]); setOpen(false);
    } catch (err) { toast.error(err.response?.data?.detail || "Gagal muat naik"); }
    finally { setBusy(""); }
  };
  const close = (o) => {
    if (o || busy) return;
    if (pages.length && !window.confirm(`Buang ${pages.length} halaman imbasan yang belum dilampirkan?`)) return;
    setOpen(false); setPages([]); setCur(null);
  };

  return (
    <>
      <input ref={ref} type="file" accept="image/*" capture="environment" hidden onChange={pick} data-testid="ai-scan-input" />
      <Button size="icon" variant="ghost" className="h-10 w-10 rounded-xl shrink-0" onClick={() => ref.current?.click()} disabled={disabled} title="Imbas dokumen (kamera)" data-testid="ai-scan-btn">
        <ScanLine className="h-5 w-5" />
      </Button>
      <Dialog open={open} onOpenChange={close}>
        <DialogContent className="max-w-md z-[10005] max-h-[92vh] overflow-y-auto" data-testid="ai-scan-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><ScanLine className="h-5 w-5 text-fuchsia-600" /> Imbas Dokumen {pages.length > 0 && <span className="text-xs font-semibold text-slate-400">· {pages.length} halaman</span>}</DialogTitle></DialogHeader>
          <PageStrip pages={pages} onRemove={(i) => setPages((p) => p.filter((_, k) => k !== i))} />
          {cur && (busy === "scan" || !cur.res) ? (
            <div className="h-56 grid place-items-center text-sm text-slate-500"><div className="text-center"><Loader2 className="h-7 w-7 animate-spin mx-auto mb-2 text-fuchsia-500" />Memotong & meluruskan dokumen...</div></div>
          ) : cur && adjust ? (
            <AICornerEditor src={cur.original} initial={cur.res.corners || cur.auto} busy={busy === "warp"} onApply={warp} onCancel={() => setAdjust(false)} />
          ) : cur ? (
            <>
              <div className="rounded-xl bg-slate-100 p-2 grid place-items-center"><img src={cur.res.image} alt="imbasan" className="max-h-[46vh] rounded-lg shadow" data-testid="ai-scan-preview" /></div>
              <div className={`text-xs font-semibold ${cur.res.found ? "text-emerald-600" : "text-amber-600"}`} data-testid="ai-scan-status">
                {cur.res.manual ? "Dipotong ikut bucu yang anda laras." : cur.res.found ? "Bucu dokumen dikesan - dipotong & diluruskan automatik." : "Bucu dokumen tidak dikesan - laras bucu sendiri atau ambil dengan latar lebih gelap."}
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Button variant="outline" className="rounded-xl text-xs" onClick={() => setAdjust(true)} disabled={!!busy || !cur.original} data-testid="ai-scan-adjust"><Crop className="h-3.5 w-3.5 mr-1" />Laras bucu</Button>
                <Button variant="outline" className="rounded-xl text-xs" onClick={() => ref.current?.click()} disabled={!!busy} data-testid="ai-scan-retake"><RotateCcw className="h-3.5 w-3.5 mr-1" />Ambil semula</Button>
                <Button variant="outline" className="rounded-xl text-xs" onClick={() => keep(false)} disabled={!!busy || !cur.original} data-testid="ai-scan-use-original"><ImageIcon className="h-3.5 w-3.5 mr-1" />Guna asal</Button>
                <Button className="rounded-xl text-xs bg-fuchsia-600 hover:bg-fuchsia-700 text-white" onClick={() => keep(true)} disabled={!!busy} data-testid="ai-scan-use"><Check className="h-3.5 w-3.5 mr-1" />Simpan halaman</Button>
              </div>
            </>
          ) : (
            <div className="space-y-2 pt-1">
              <div className="text-[12px] text-slate-500 text-center">{full ? "Had 10 lampiran dicapai." : "Imbas halaman seterusnya atau tekan Selesai untuk lampirkan ke chat."}</div>
              <div className="grid grid-cols-2 gap-2">
                <Button variant="outline" className="rounded-xl text-xs" onClick={() => ref.current?.click()} disabled={!!busy || full} data-testid="ai-scan-next"><Plus className="h-3.5 w-3.5 mr-1" />Halaman seterusnya</Button>
                <Button className="rounded-xl text-xs bg-fuchsia-600 hover:bg-fuchsia-700 text-white" onClick={() => finish(false)} disabled={!!busy || !pages.length} data-testid="ai-scan-done">
                  {busy === "done" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><CheckCheck className="h-3.5 w-3.5 mr-1" />Selesai ({pages.length})</>}
                </Button>
              </div>
              {onToWord && (
                <Button className="w-full rounded-xl text-xs bg-sky-600 hover:bg-sky-700 text-white" onClick={() => finish(true)} disabled={!!busy || !pages.length} data-testid="ai-scan-to-word">
                  {busy === "word" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><FileType2 className="h-3.5 w-3.5 mr-1" />Tukar ke Word boleh edit ({pages.length} halaman)</>}
                </Button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
